import React, { useState } from 'react';
import { Check } from 'lucide-react';

export interface BaseInteractiveProps {
  exercise: any;
  onAnswer: (answer: any) => void;
  showFeedback?: boolean;
  disabled?: boolean;
}

// Slider Component for simulations
export const SliderComponent: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  const [sliderValue, setSliderValue] = useState(exercise.initialValue || 50);
  const [hasInteracted, setHasInteracted] = useState(false);

  const handleSliderChange = (value: number) => {
    if (disabled) return;
    setSliderValue(value);
    setHasInteracted(true);
    onAnswer(value);
  };

  // Calculate dynamic feedback based on slider value
  const getDynamicFeedback = () => {
    if (!exercise.dynamicFeedback) return null;

    // Find the appropriate feedback range
    for (const range of exercise.dynamicFeedback) {
      if (sliderValue >= range.min && sliderValue <= range.max) {
        return range.feedback;
      }
    }
    return null;
  };

  const dynamicFeedback = getDynamicFeedback();

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-gray-700 mb-3">{exercise.content}</p>
        <p className="font-medium text-gray-900 mb-3">{exercise.question}</p>
      </div>

      {/* Slider Interface */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {exercise.sliderLabel || 'Adjust the value'}
          </label>
          <div className="relative">
            <input
              type="range"
              min={exercise.min || 0}
              max={exercise.max || 100}
              value={sliderValue}
              onChange={(e) => handleSliderChange(Number(e.target.value))}
              disabled={disabled}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${sliderValue}%, #e5e7eb ${sliderValue}%, #e5e7eb 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{exercise.minLabel || exercise.min || 0}</span>
              <span className="font-medium text-gray-700">
                {exercise.valueFormatter ? exercise.valueFormatter(sliderValue) : sliderValue}
              </span>
              <span>{exercise.maxLabel || exercise.max || 100}</span>
            </div>
          </div>
        </div>

        {/* Real-time feedback */}
        {hasInteracted && dynamicFeedback && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">{dynamicFeedback}</p>
          </div>
        )}

        {/* Visual representation (if provided) */}
        {exercise.visualization && (
          <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg">
            <div className="text-center">
              {/* Generic visualization title */}
              {exercise.visualization.title && (
                <div className="text-sm text-gray-600 mb-3">{exercise.visualization.title}</div>
              )}

              {/* Flexible visualization based on type */}
              {exercise.visualization.type === 'dual_values' && exercise.visualization.values && (
                <div className="flex justify-center space-x-4">
                  {exercise.visualization.values.map((valueConfig: any, index: number) => {
                    // Calculate value based on slider position and configuration
                    let displayValue;
                    if (valueConfig.calculation === 'inverse') {
                      displayValue = Math.round(100 - sliderValue);
                    } else if (valueConfig.calculation === 'direct') {
                      displayValue = Math.round(sliderValue);
                    } else if (valueConfig.calculation === 'formula' && valueConfig.formula) {
                      // Support custom formulas (e.g., "value * 2", "100 - value")
                      displayValue = Math.round(eval(valueConfig.formula.replace('value', sliderValue.toString())));
                    } else {
                      displayValue = sliderValue;
                    }

                    return (
                      <div key={index} className="text-center">
                        <div className={`w-16 h-16 ${valueConfig.bgColor || 'bg-blue-100'} rounded-full flex items-center justify-center mb-1`}>
                          <span className={`text-sm font-medium ${valueConfig.textColor || 'text-blue-800'}`}>
                            {valueConfig.formatter ? valueConfig.formatter.replace('{value}', displayValue.toString()) : `${displayValue}${valueConfig.unit || ''}`}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600">{valueConfig.label}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Bar chart visualization */}
              {exercise.visualization.type === 'bar_chart' && exercise.visualization.bars && (
                <div className="space-y-2">
                  {exercise.visualization.bars.map((bar: any, index: number) => {
                    let barValue;
                    if (bar.calculation === 'inverse') {
                      barValue = 100 - sliderValue;
                    } else if (bar.calculation === 'direct') {
                      barValue = sliderValue;
                    } else if (bar.calculation === 'formula' && bar.formula) {
                      barValue = eval(bar.formula.replace('value', sliderValue.toString()));
                    } else {
                      barValue = sliderValue;
                    }

                    return (
                      <div key={index} className="flex items-center gap-3">
                        <div className="w-20 text-xs text-gray-600 text-right">{bar.label}</div>
                        <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                          <div
                            className={`h-4 ${bar.color || 'bg-blue-500'} rounded-full transition-all duration-300`}
                            style={{ width: `${Math.max(0, Math.min(100, barValue))}%` }}
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
                            {Math.round(barValue)}{bar.unit || '%'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Simple text display */}
              {exercise.visualization.type === 'text_display' && exercise.visualization.display && (
                <div className="text-center">
                  <div className={`text-2xl font-bold ${exercise.visualization.display.textColor || 'text-gray-900'} mb-1`}>
                    {exercise.visualization.display.formatter
                      ? exercise.visualization.display.formatter.replace('{value}', sliderValue.toString())
                      : `${sliderValue}${exercise.visualization.display.unit || ''}`
                    }
                  </div>
                  {exercise.visualization.display.subtitle && (
                    <div className="text-sm text-gray-600">{exercise.visualization.display.subtitle}</div>
                  )}
                </div>
              )}

              {/* Custom HTML content */}
              {exercise.visualization.type === 'custom' && exercise.visualization.content && (
                <div dangerouslySetInnerHTML={{
                  __html: exercise.visualization.content.replace(/\{value\}/g, sliderValue.toString())
                }} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Final explanation after interaction */}
      {showFeedback && hasInteracted && exercise.explanation && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800 mb-1">Explanation:</p>
              <p className="text-sm text-green-700">{exercise.explanation}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};